import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Bookmark, Compass, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";

export default function Dashboard() {
  const { user } = useAuth();
  const [saved, setSaved] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [recent, setRecent] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/saved-areas").then((r) => setSaved(r.data.items || [])).catch(() => {});
    api.get("/alerts").then((r) => setAlerts(r.data.items || [])).catch(() => {});
    api
      .post("/recommendations", {
        origin_lat: user?.profile?.home_lat ?? null,
        origin_lon: user?.profile?.home_lon ?? null,
        date: new Date().toISOString().slice(0, 10),
        max_travel_minutes: user?.profile?.max_travel_minutes || 90,
      })
      .then((r) => setRecent((r.data.items || []).slice(0, 3)))
      .catch(() => {});
  }, [user]);

  const removeSaved = async (id) => {
    await api.delete(`/saved-areas/${id}`);
    setSaved((s) => s.filter((a) => a.id !== id));
    toast.success("Removed");
  };
  const removeAlert = async (id) => {
    await api.delete(`/alerts/${id}`);
    setAlerts((s) => s.filter((a) => a.id !== id));
  };
  const createAlert = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/alerts", {
        min_score: 70,
        frequency: "daily",
        channel: "in_app",
      });
      setAlerts((s) => [data, ...s]);
      toast.success("Alert created");
    } catch {
      toast.error("Please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-8 md:py-12" data-testid="dashboard-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline">Dashboard</div>
          <h1 className="font-display text-3xl md:text-4xl text-teal-900 mt-2">
            Welcome back{user?.name ? `, ${user.name}` : ""}.
          </h1>
        </div>
        <Link
          to="/onboarding"
          data-testid="edit-profile-link"
          className="text-sm text-teal-800 hover:underline"
        >
          Edit surf profile
        </Link>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card-soft p-6">
          <div className="flex items-center gap-2">
            <Compass className="w-5 h-5 text-teal-800" />
            <h2 className="font-display text-xl text-teal-900">Best nearby, today</h2>
          </div>
          {recent.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">
              Set a home location in your profile to see today's picks.
            </p>
          ) : (
            <ul className="mt-4 space-y-3" data-testid="dashboard-recent">
              {recent.map((r) => (
                <li key={r.surf_area_id} className="flex items-center justify-between gap-3 rounded-xl border border-border p-3 bg-white">
                  <Link to={`/surf-area/${r.surf_area_slug}`} className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-teal-900">{r.surf_area_name}</div>
                    <div className="text-xs text-muted-foreground">
                      Score {r.score} · {r.suitability.replace("_", " ")}
                    </div>
                  </Link>
                  <div className="font-display text-2xl text-teal-900">{r.score}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card-soft p-6">
          <div className="flex items-center gap-2">
            <Bookmark className="w-5 h-5 text-teal-800" />
            <h2 className="font-display text-xl text-teal-900">Saved areas</h2>
          </div>
          {saved.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">Save any surf area from its detail page.</p>
          ) : (
            <ul className="mt-4 space-y-2" data-testid="dashboard-saved">
              {saved.map((a) => (
                <li key={a.id} className="flex items-center justify-between gap-3 rounded-xl border border-border p-3 bg-white">
                  <Link to={`/surf-area/${a.slug}`} className="text-sm text-teal-900 font-medium hover:underline">
                    {a.name}
                  </Link>
                  <button
                    onClick={() => removeSaved(a.id)}
                    data-testid={`saved-remove-${a.id}`}
                    className="text-muted-foreground hover:text-terracotta-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card-soft p-6 md:col-span-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-teal-800" />
              <h2 className="font-display text-xl text-teal-900">Alerts</h2>
            </div>
            <button
              type="button"
              onClick={createAlert}
              disabled={busy}
              data-testid="create-alert-btn"
              className="h-9 px-4 rounded-full text-sm bg-teal-800 text-white hover:bg-teal-900 disabled:opacity-60"
            >
              Alert me when score ≥ 70
            </button>
          </div>
          {alerts.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">
              You have no active alerts. Create one to hear about promising windows.
            </p>
          ) : (
            <ul className="mt-4 space-y-2" data-testid="dashboard-alerts">
              {alerts.map((a) => (
                <li key={a.id} className="flex items-center justify-between gap-3 rounded-xl border border-border p-3 bg-white">
                  <div className="text-sm text-teal-900">
                    Min score {a.min_score} · {a.frequency} · {a.channel}
                  </div>
                  <button
                    onClick={() => removeAlert(a.id)}
                    data-testid={`alert-remove-${a.id}`}
                    className="text-muted-foreground hover:text-terracotta-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
